import { ApplicationDependencyService, SchemaService } from '@amzn/awscat-portfolio-assessment-service-client';
import { AppDependency, AppToAppBulkDeleteParams, AppToAppBulkUpdateParams, SchemaProperty } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { SortingState } from '@amzn/awsui-collection-hooks/dist/cjs/interfaces';
import { SpaceBetween } from '@amzn/awsui-components-react';
import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { match, useRouteMatch } from 'react-router-dom';
import { useIsMounted } from 'react-tidy';

import { constants } from '../../common/Localization';
import AWSCATTable from '../../common/table/Table-index';
import { ColumnDefinition } from '../../common/table/types';
import { MatchParam } from '../../common/types';
import { deepCopy, generatePluralStr } from '../../common/util';
import PortfolioTableActionGroup from './common/PortfolioTableActionGroup';
import { ColumnOrderType, EXCLUDED_APP_DEPENDENCY_COLUMNS } from './constants';
import { InlineEditContext, InlineEditRowItem, InlineEditValue } from './inline-editing/InlineEditContext';
import { SchemaPropertyMap } from './types';
import { generateColumnDefs } from './utils';

type Props = FlashContextInterface;

const appDepService = new ApplicationDependencyService();
const schemaService = new SchemaService();
const resourceName = 'application-dependencies';

const AppDependencyPage: FunctionComponent<Props> = ({flash}) => {
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId',
    });

    const portfolioId = match?.params?.portfolioId;
    const isMounted = useIsMounted();
    const [ items, setItems ] = useState<AppDependency[]>([]);
    const [ schemas, setSchemas ] = useState<SchemaProperty[]>([]);
    const [ schemaMap, setSchemaMap ] = useState<SchemaPropertyMap>({});
    const [ columnDef, setColumnDef ] = useState<ColumnDefinition<AppDependency>[]>([]);
    const [ selectedItems, setSelectedItems ] = useState<AppDependency[]>([]);
    const [ filteredItems, setFilteredItems ] = useState<AppDependency[] | null>(null);
    const [ loading, setLoading ] = useState(true);
    const { updateInlineEditFn } = useContext(InlineEditContext);

    const loadData = async () => {
        setLoading(true);
        setSelectedItems([]);
        try {
            if (!portfolioId) {
                flash.alert(FlashType.error, constants.INVALID_PORTFOLIO_ID_ERROR, '');
                return;
            }
            // Load data
            const appDependencies = await appDepService.listImportOnly(portfolioId);
            if (!isMounted()) {
                return;
            }
            setItems(appDependencies);

            // Load schema
            const schemas = await schemaService.listAttributes(resourceName, portfolioId);
            if (!isMounted()) {
                return;
            }
            setSchemas(schemas);
            const appDepHeaders: SchemaPropertyMap = {};
            schemas.forEach(schema => {
                appDepHeaders[schema.value] = schema;
            });
            setSchemaMap(appDepHeaders);

            // Build column definitions
            const columnDefinitions = generateColumnDefs(appDependencies, appDepHeaders, 
                ColumnOrderType.APPLICATION_DEPENDENCY, EXCLUDED_APP_DEPENDENCY_COLUMNS, true);
            setColumnDef(columnDefinitions);
        } catch (err) {
            if (!isMounted()) {
                return;
            }
            flash.alert(FlashType.error, (err as Error).message, constants.APP_DEP_ERROR_LOADING);
            setItems([]);
            setColumnDef([]);
        } finally {
            if (!isMounted()) {
                return;
            }
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [portfolioId]);


    const inlineEditFunction = async (rowItem: InlineEditRowItem, columnId: string, updatedValue: InlineEditValue) => {
        if (!portfolioId) {
            flash.alert(FlashType.error, constants.INVALID_PORTFOLIO_ID_ERROR, '');
            return;
        }

        const conditions: AppToAppBulkUpdateParams = {
            fieldName: columnId,
            fieldValue: updatedValue,
            portfolioId,
            applicationDependencyIds: [{
                applicationDependencyId: (rowItem as AppDependency).applicationDependencyId,
                portfolioId,
            }],
        };

        try {
            await appDepService.update(conditions);
            const updateSuccessMsg = generatePluralStr(1, constants.APP_DEP_RESOURCE_NAME, '', true)
                + ' ' + constants.BULK_UPDATE_SUCCESS_HEADING;
            flash.alert(FlashType.success, updateSuccessMsg, '');
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.APP_DEP_EDITING_ERROR_HEADING);
        } finally {
            await loadData();
        }
    };

    useEffect(() => {
        updateInlineEditFn(inlineEditFunction);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Delete multiple items. If items are selected, they will be deleted. If there are no selected items,
     * then all filtered items will be deleted.
     * @returns void
     */    
    const onConfirmBulkDeletion = async () => {
        if (!portfolioId) {
            flash.alert(FlashType.error, constants.INVALID_PORTFOLIO_ID_ERROR, '');
            return;
        }
        setLoading(true);
        try {
            const selectedIds = (selectedItems.length || !filteredItems ? selectedItems : filteredItems)
                .map(selectedItem => ({
                    applicationDependencyId: selectedItem.applicationDependencyId,
                    portfolioId,
                }));
            const conditions: AppToAppBulkDeleteParams = {
                portfolioId,
                applicationDependencyIds: selectedIds,
            };
            await appDepService.deleteMappings(conditions); 
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.APP_DEP_DELETE_ERROR_HEADING);
        } finally {
            await loadData();
        }
    };

    const onConfirmDeleteAll = async () => {
        if (!portfolioId) {
            flash.alert(FlashType.error, constants.INVALID_PORTFOLIO_ID_ERROR, '');
            return;
        }
        setLoading(true);
        try {
            await appDepService.clear(portfolioId); 
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.APP_DEP_DELETE_ERROR_HEADING);
        } finally {
            await loadData();
        }
    };

    const onConfirmBulkEdit = async (
        selectAll: boolean,
        selectedHeader: string,
        updatedValue: string | number | boolean,
    ) => {
        if (!portfolioId) {
            flash.alert(FlashType.error, constants.INVALID_PORTFOLIO_ID_ERROR, '');
            return;
        }
        setLoading(true);
        const selectedSchema = schemaMap[selectedHeader];
        if (!selectedHeader || !selectedSchema) {
            flash.alert(FlashType.error, constants.BULK_UPDATE_ERROR_CONTENT, constants.APP_DEP_EDITING_ERROR_HEADING);
            return;
        }
        const itemsToUpdate = selectAll ? (filteredItems ?? items) : selectedItems;
        const selectedIds = itemsToUpdate.map(item => ({
            applicationDependencyId: item.applicationDependencyId,
            portfolioId,
        }));
        
        const conditions: AppToAppBulkUpdateParams = {
            fieldName: selectedHeader,
            fieldValue: updatedValue,
            portfolioId,
            applicationDependencyIds: selectedIds,
        };

        try {
            await appDepService.update(conditions);
            const updateSuccessMsg = generatePluralStr(selectedIds.length || items.length, constants.APP_DEP_RESOURCE_NAME, '', true)
                + ' ' + constants.BULK_UPDATE_SUCCESS_HEADING;
            flash.alert(FlashType.success, updateSuccessMsg, '');
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.APP_DEP_EDITING_ERROR_HEADING);
        } finally {
            await loadData();
        }
    };

    const defaultSortingState: SortingState<AppDependency> = {
        isDescending: false,
        sortingColumn: {
            sortingField: 'applicationDependencyId',
        },
    };

    const filterChanged = (items: readonly AppDependency[] | null) => {
        setFilteredItems(deepCopy(items));
    };

    return (
        <SpaceBetween size="m">
            <AWSCATTable
                resourceName={constants.APP_DEP_TABLE_HEADER}
                resourceType='applicationDependency'
                items={items}
                importWhenEmpty={true}
                columnDefinitions={columnDef}
                loading={loading}
                resizeable={false} // explicitly not allowing resize since there are few columns and all get crammed to the left
                autoGenerateVisibleContent={true}
                onSelectionChange={setSelectedItems as () => void}
                onFilterChange={filterChanged}
                headerActions={
                    <PortfolioTableActionGroup
                        resourceType='applicationDependency'
                        resourceName={constants.APP_DEP_RESOURCE_NAME}
                        resourceCount={items.length}
                        selectionCount={selectedItems.length}
                        hasFilter={!!filteredItems}
                        filteredCount={filteredItems?.length ?? 0}
                        loading={loading}
                        onConfirmDeletion={onConfirmBulkDeletion}
                        onConfirmDeleteAll={onConfirmDeleteAll}
                        onConfirmBulkEdit={onConfirmBulkEdit}
                        schemaProperties={schemas}
                        schemaPropertyMap={schemaMap}
                    />
                }
                selectionType='multi'
                defaultSortBy={defaultSortingState}
                useDefaultDownload
            />
        </SpaceBetween>
    );
};

export default withFlashContext(AppDependencyPage);
